import * as React from 'react';
import { Box, Button, Paper, Skeleton, Typography } from '@mui/material';
import { Bank as BankIcon, Plus as PlusIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';

import { getAmenities } from '@/api/amenities';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { useDialog } from '@/hooks/v2/use-dialog';
import { useUser } from '@/hooks/v2/use-user';
import { hasAnyAccountRoles, hasCommunityRolesV2 } from '@/utils/has-roles';

import { AmenitiesTable } from './components/amenities-table';
import { CreateAmenityDialog } from './components/create-amenity-dialog';

export function CommunityAmenitiesPage(): React.JSX.Element {
  const dialog = useDialog();
  const { account, community } = useUser();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['amenities', community?.id],
    queryFn: () => getAmenities(community?.id || ''),
    enabled: !!community?.id,
  });

  const handleOnSuccess = React.useCallback(async () => {
    await refetch();
    dialog.handleClose();
  }, [dialog, refetch]);

  const amenities = data?.amenities ?? [];
  const canManageAmenities = hasCommunityRolesV2(community, [
    COMMUNITY_ROLES.COMMUNITY_MANAGER,
    COMMUNITY_ROLES.BOARD_MEMBER,
  ]) || hasAnyAccountRoles(account!, [
    ACCOUNT_ROLES.ACCOUNT_OWNER,
    ACCOUNT_ROLES.ACCOUNT_ADMIN,
    ACCOUNT_ROLES.ACCOUNT_MANAGER,
  ]);

  if (isLoading) {
    return (
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 3 }}>
          <Skeleton height={32} width={200} variant="text" />
          <Skeleton height={20} width={300} variant="text" sx={{ mt: 1 }} />
        </Box>
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} height={52} variant="rectangular" sx={{ mx: 2, mb: 1, borderRadius: 1 }} />
        ))}
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 6, textAlign: 'center' }}>
        <Typography color="error" variant="body1">Failed to load amenities</Typography>
      </Paper>
    );
  }

  if (!amenities.length) {
    return (
      <>
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 6, textAlign: 'center' }}>
          <BankIcon size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>No amenities yet</Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mb: canManageAmenities ? 2.5 : 0 }}>
            {canManageAmenities
              ? 'Create your first amenity to allow residents to make reservations.'
              : 'No amenities have been set up for this community yet.'}
          </Typography>
          {canManageAmenities ? (
            <Button
              onClick={dialog.handleOpen}
              startIcon={<PlusIcon size={16} />}
              variant="contained"
              sx={{ fontWeight: 600, textTransform: 'none' }}
            >
              Add Amenity
            </Button>
          ) : null}
        </Paper>
        {canManageAmenities ? (
          <CreateAmenityDialog open={dialog.open} onClose={dialog.handleClose} onSuccess={handleOnSuccess} />
        ) : null}
      </>
    );
  }

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      <AmenitiesTable loading={isLoading} onCreated={handleOnSuccess} rows={amenities} />
    </Paper>
  );
}
